import React from 'react';
import { Loader2, Save, Check, Plus, X, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SharedRigGallery from '@/components/admin/SharedRigGallery';
import RigHeroMedia from '@/components/admin/RigHeroMedia';
import { inputCls, labelCls, CATEGORIES, RATINGS, SPECS } from './rigConstants';

/**
 * Showcase Page tab of the RigDetailEditor — owns no state, renders the public
 * showcase page form from props passed down by the parent editor.
 */
export default function RigShowcaseTab({
  scLoading,
  showcase,
  resolvedKey,
  name,
  onAiFill, aiLoading,
  onRemoveShowcase,
  sc, onScField,
  galleryImages, onGalleryImages,
  gameInput, onGameInput, onAddGame,
  saving, saved, onSave,
}) {
  if (scLoading) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-white/40 text-xs">
          {showcase ? <span className="text-primary">Showcase page live at /simulators/{showcase.slug || resolvedKey}</span> : 'No showcase page yet — fill in the details and save to publish one.'}
        </p>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onAiFill} disabled={aiLoading || !name}
            className="flex items-center gap-1.5 text-xs font-bold tracking-wider px-3 py-2 border border-primary/30 text-primary hover:bg-primary/10 transition-colors disabled:opacity-40">
            {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {aiLoading ? 'GENERATING' : 'AI AUTO-FILL'}
          </button>
          {showcase && (
            <button type="button" onClick={onRemoveShowcase} className="text-xs font-bold tracking-wider text-red-400/80 hover:text-red-400">REMOVE SHOWCASE PAGE</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className={labelCls}>CATEGORY</Label>
          <select value={sc.category} onChange={(e) => onScField('category', e.target.value)} className="w-full bg-[#111115] border-white/10 text-white h-11 px-3 focus:border-primary/60">
            {CATEGORIES.map((c) => <option key={c.key} value={c.key} className="bg-[#111115]">{c.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label className={labelCls}>SHORT DESCRIPTION</Label>
          <Input value={sc.short_description} onChange={(e) => onScField('short_description', e.target.value)} className={inputCls} placeholder="One-line tagline" />
        </div>
      </div>

      <div>
        <Label className={labelCls}>FULL DESCRIPTION</Label>
        <Textarea value={sc.full_description} onChange={(e) => onScField('full_description', e.target.value)} rows={3} className="bg-[#111115] border-white/10 text-white focus:border-primary/60" />
      </div>

      {/* Media */}
      <div className="border-t border-white/5 pt-4">
        <p className="text-primary text-[10px] font-bold tracking-widest mb-3">MEDIA</p>
        <RigHeroMedia
          photos={galleryImages}
          heroImageUrl={sc.hero_image_url}
          onHeroImageUrl={(url) => onScField('hero_image_url', url)}
          heroVideoUrl={sc.hero_video_url}
          onHeroVideoUrl={(url) => onScField('hero_video_url', url)}
        />
        <Label className={labelCls}>GALLERY (shared with booking details)</Label>
        <p className="text-white/30 text-[10px] mb-2">Same library as the Booking Details tab. Tick "Showcase" on a photo to show it on the public /simulators page.</p>
        <SharedRigGallery photos={galleryImages} onChange={onGalleryImages} label="DROP PHOTOS HERE OR CLICK TO BROWSE" />
        <div className="mt-3">
          <Label className={labelCls}>SHOWCASE VIDEO (YOUTUBE / VIMEO)</Label>
          <Input value={sc.showcase_video_url} onChange={(e) => onScField('showcase_video_url', e.target.value)} className={inputCls} placeholder="https://youtube.com/watch?v=..." />
        </div>
      </div>

      {/* Ratings */}
      <div className="border-t border-white/5 pt-4">
        <p className="text-primary text-[10px] font-bold tracking-widest mb-3">RATINGS (0–10)</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {RATINGS.map((r) => (
            <div key={r.key}>
              <Label className={labelCls}>{r.label}</Label>
              <Input type="number" min="0" max="10" step="0.1" value={sc[r.key]} onChange={(e) => onScField(r.key, e.target.value)} className={inputCls} />
            </div>
          ))}
        </div>
      </div>

      {/* Specs */}
      <div className="border-t border-white/5 pt-4">
        <p className="text-primary text-[10px] font-bold tracking-widest mb-3">TECHNICAL SPECS</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {SPECS.map((s) => (
            <div key={s.key}>
              <Label className={labelCls}>{s.label}</Label>
              <Input value={sc[s.key]} onChange={(e) => onScField(s.key, e.target.value)} className={inputCls} />
            </div>
          ))}
        </div>
      </div>

      {/* Experience + games */}
      <div className="border-t border-white/5 pt-4">
        <p className="text-primary text-[10px] font-bold tracking-widest mb-3">EXPERIENCE & GAMES</p>
        <div className="mb-4">
          <Label className={labelCls}>EXPERIENCE TEXT</Label>
          <Textarea value={sc.experience_text} onChange={(e) => onScField('experience_text', e.target.value)} rows={3} className="bg-[#111115] border-white/10 text-white focus:border-primary/60" />
        </div>
        <Label className={labelCls}>SUPPORTED GAMES</Label>
        <div className="flex gap-2 mb-2">
          <Input value={gameInput} onChange={(e) => onGameInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onAddGame())} className={inputCls} placeholder="Add a game title" />
          <button type="button" onClick={onAddGame} className="bg-white/5 border border-white/10 hover:border-primary/40 text-white/70 hover:text-white px-3 text-xs font-bold inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> ADD
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(sc.supported_games || []).map((g, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/20 text-primary text-xs font-bold px-2.5 py-1">
              {g}
              <button type="button" onClick={() => onScField('supported_games', (sc.supported_games || []).filter((_, idx) => idx !== i))} className="text-primary/60 hover:text-primary"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      </div>

      {/* Settings */}
      <div className="border-t border-white/5 pt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className={labelCls}>BOOKING URL</Label>
          <Input value={sc.booking_url} onChange={(e) => onScField('booking_url', e.target.value)} className={inputCls} placeholder="/bookings" />
        </div>
        <div>
          <Label className={labelCls}>SORT ORDER</Label>
          <Input type="number" value={sc.sort_order} onChange={(e) => onScField('sort_order', e.target.value)} className={inputCls} />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={sc.featured} onChange={(e) => onScField('featured', e.target.checked)} className="w-4 h-4 accent-primary" />
            <span className="text-white/60 text-xs font-bold tracking-wide">FEATURED ON HOMEPAGE</span>
          </label>
        </div>
      </div>

      <div className="flex items-center justify-end pt-3 border-t border-white/5">
        <button onClick={onSave} disabled={saving}
          className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-5 py-2.5 transition-all disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saving ? 'SAVING' : saved ? 'SAVED' : (showcase ? 'SAVE SHOWCASE' : 'PUBLISH SHOWCASE')}
        </button>
      </div>
    </div>
  );
}